import { useState } from 'react';
import { useAuditVerify, useCreateExport, useExportJobs, useExportLink } from '@/lib/query';
import type { ExportJob } from '@shared/domain';
import { int, ts } from '@/lib/format';
import { Badge, Card, DataTable, SectionTitle } from '@/components/ui';
import { PageBanner } from '@/components/layout/PageBanner';
import { Icon } from '@/components/layout/Icons';

function StatusCell({ j }: { j: ExportJob }) {
  if (j.status === 'queued') return <Badge className="bg-slate-100 text-slate-500 border-slate-200">排队中</Badge>;
  if (j.status === 'failed') return <Badge className="bg-alarm-bg text-alarm-fg border-alarm-border">失败</Badge>;
  if (j.status === 'succeeded') return <Badge className="bg-ok-bg text-ok-fg border-ok-border">已完成</Badge>;
  return <Badge className="bg-info-bg text-info-fg border-info-border">执行中</Badge>;
}

function Progress({ j }: { j: ExportJob }) {
  if (j.status === 'failed') return <div className="mt-1 text-[12px] text-alarm-fg">{j.error}</div>;
  if (j.status === 'queued') return null;
  if (j.progress === null) {
    return (
      <div className="mt-1 text-[12px] text-slate-400">
        已处理行数：{int(j.processedRows ?? 0)} · 无法预估总数（不确定进度）
      </div>
    );
  }
  return (
    <div className="mt-1 w-48">
      <div className="flex items-center justify-between text-[12px] text-slate-500">
        <span>已处理 {int(j.processedRows ?? 0)}</span>
        <span className="tabular-nums">{j.progress}%</span>
      </div>
      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-100">
        <div className="h-full rounded-full bg-action-500" style={{ width: `${j.progress}%` }} />
      </div>
      {j.estimatedTotalRows ? <div className="mt-1 text-[12px] text-slate-400">预估总行数 {int(j.estimatedTotalRows)}</div> : null}
    </div>
  );
}

export default function ComplianceExport() {
  const { data: jobs } = useExportJobs();
  const create = useCreateExport();
  const link = useExportLink();
  const [purpose, setPurpose] = useState('');
  const [dataset, setDataset] = useState('alarms');

  const submit = () => {
    if (!purpose.trim()) return;
    create.mutate({ purpose: purpose.trim(), format: 'CSV_GZIP', dataset });
    setPurpose('');
  };
  const download = (id: string) => {
    // Use a programmatic <a> click, not window.open: the signed-link fetch is
    // async, so by the time it resolves the browser no longer treats the action
    // as user-initiated and popup-blocks window.open (the "no reaction" bug).
    link
      .mutateAsync(id)
      .then(({ url }) => {
        const a = document.createElement('a');
        a.href = url;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
      })
      .catch((e) => window.alert(`获取下载链接失败：${(e as Error)?.message ?? '未知错误'}`));
  };

  return (
    <div>
      <PageBanner />
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-[24px] font-bold text-slate-900">合规数据导出中心</h2>
          <p className="mt-1 text-[14px] text-slate-500">异步海量数据提取与审计留存 · PR5 Job / Artifact</p>
        </div>
        <button onClick={submit} disabled={!purpose.trim() || create.isPending} className="btn btn-primary disabled:opacity-50">
          <Icon name="plus" size={16} /> 新建导出任务
        </button>
      </div>

      <div className="mb-5 flex flex-wrap items-center gap-3">
        <select value={dataset} onChange={(e) => setDataset(e.target.value)} className="rounded-lg border border-hairline px-3 py-2 text-sm">
          <option value="alarms">数据集：告警</option>
          <option value="lab">数据集：化验</option>
          <option value="equipment">数据集：设备台账</option>
          <option value="production">数据集：生产批次</option>
          <option value="production-report">数据集：生产报表（存活/FCE/投喂/损耗）</option>
        </select>
        <input value={purpose} onChange={(e) => setPurpose(e.target.value)} placeholder="导出目的（如：环保局 2026 半年度排污审计）" className="min-w-[280px] flex-1 rounded-lg border border-hairline px-3 py-2 text-sm" />
        <button onClick={submit} disabled={!purpose.trim() || create.isPending} className="btn btn-primary disabled:opacity-50">{create.isPending ? '创建中…' : '新建导出任务'}</button>
      </div>

      <div className="mb-4 rounded-lg border border-ok-border bg-ok-bg/50 px-4 py-2.5 text-[13px] text-ok-fg">
        过期判定与下载可用性已绑定服务端时间 + performance.now() 偏移，不使用本机 Date.now() 做安全决策。
      </div>

      <SectionTitle title="导出任务" />
      <Card className="p-0">
        <DataTable
          columns={[
            {
              key: 'purpose',
              header: '任务信息 · 审计',
              render: (j: ExportJob) => (
                <div>
                  <div className="text-[12px] text-slate-400">导出目的</div>
                  <div className="text-[14px] font-medium text-slate-800">{j.purpose}</div>
                  <div className="mt-1 flex items-center gap-2 text-[12px] text-slate-400">
                    <span className="font-mono">{j.expId}</span>
                    <Badge className="bg-info-bg text-info-fg border-info-border">🛡 {j.auditId}</Badge>
                  </div>
                  <div className="text-[12px] text-slate-400">发起人: {j.initiator}</div>
                </div>
              ),
            },
            { key: 'format', header: '格式', render: (j: ExportJob) => <Badge className="font-mono">{j.format}</Badge> },
            { key: 'status', header: '状态 · 进度', render: (j: ExportJob) => (<div><StatusCell j={j} /><Progress j={j} /></div>) },
            {
              key: 'time',
              header: '创建 / 保管期限',
              render: (j: ExportJob) => (
                <div className="text-[13px] text-slate-600">
                  {ts(j.createdAt, false)}
                  <div className="text-[12px] text-slate-400">对象保管截止:</div>
                  <div className="text-[12px] text-slate-400">{ts(j.retentionUntil, false)}</div>
                </div>
              ),
            },
            { key: 'ops', header: '操作', render: (j: ExportJob) => (j.status === 'succeeded' && j.artifactPath ? <button onClick={() => download(j.id)} className="text-brand-600 hover:underline">下载</button> : <span className="text-slate-300">—</span>) },
          ]}
          rows={jobs ?? []}
        />
      </Card>

      <AuditChainCard />
    </div>
  );
}

/** I4: tamper tripwire — server re-derives the audit hash-chain; 30s poll. */
function AuditChainCard() {
  const { data, isError, isFetching, refetch } = useAuditVerify();
  return (
    <Card className="mt-6 p-5">
      <SectionTitle
        title={<span className="flex items-center gap-2"><Icon name="compliance" size={18} /> 审计链完整性（防篡改哈希链）</span>}
        desc="每行审计记录以 SHA-256 链接上一行；任何改写/删除都会使链无法复现。服务端周期性重算，异常触发 Prometheus AuditChainBroken。"
        right={<button onClick={() => refetch()} className="btn btn-ghost text-[13px] disabled:opacity-50" disabled={isFetching}>{isFetching ? '校验中…' : '重新校验'}</button>}
      />
      {isError ? (
        <div className="text-[13px] text-slate-400">当前账号无审计查看权限（需 audit.view 能力）。</div>
      ) : !data ? (
        <div className="text-[13px] text-slate-400">校验中…</div>
      ) : data.valid ? (
        <div className="flex flex-wrap items-center gap-3 text-[13px]">
          <Badge className="bg-ok-bg text-ok-fg border-ok-border">链完整</Badge>
          <span className="text-slate-500">已验证 <b className="tabular-nums text-slate-800">{data.checked}</b> 条密封记录</span>
          <span className="text-slate-400">链头 <code className="font-mono">{(data.headHash ?? '—').slice(0, 12)}</code></span>
          <span className="text-slate-400">· {ts(data.generatedAt, true)}</span>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-3 text-[13px]">
          <Badge className="bg-alarm-bg text-alarm-fg border-alarm-border">检测到篡改</Badge>
          <span className="text-alarm-fg">{data.reason}</span>
          <span className="text-slate-500">首个异常记录 <code className="font-mono">{data.firstBadId}</code>（此前 {data.checked} 条正常）</span>
        </div>
      )}
    </Card>
  );
}
